/**
 * Quotation comparison, technical scoring and negotiation rounds (FSD 4.2.4, P45 PS6-U69)
 * — the feature's own wrappers over the generated client (B73's operations).
 *
 * Every write takes the caller's held `idempotencyKey` (X1: one key per submission, held by
 * the screen); nothing here mints one.
 *
 * ## Sealed values reach this layer only as the server returns them
 *
 * A comparison cannot be built while the RFQ is sealed (`ERR_RFQ_SEALED_BEFORE_DEADLINE`),
 * so every price in a `ProcurementComparisonDTO` is one the server already revealed. The
 * sealed state itself is read through the sealed-aware quote reader (`listQuotes` in
 * `rfq/api.ts`): a quote row without `totalAmount` is the only evidence of a seal, and the
 * screen never derives a price from anything else.
 *
 * ## Nothing here does money arithmetic
 *
 * Normalised prices, landed cost, TCO, scores and ranks are the server's decimal strings and
 * are shown as they arrive. The only client-side sum in the award flow is the quantity
 * footing of a split (exact decimal strings, `c7/keys.ts`).
 */

import {
  procurementComparisonGet,
  procurementComparisonLandedCost,
  procurementComparisonScoresRecord,
  procurementComparisonScoresSubmit,
  procurementRfqCancelAwardProcess,
  procurementRfqComparisonBuild,
  procurementRfqComparisonVersions,
  procurementRfqNegotiate,
  procurementRfqNegotiations,
  procurementRfqQuotesLogistics,
  type ProcurementComparisonBuildRequest,
  type ProcurementComparisonDTO,
  type ProcurementComparisonLandedCostRequest,
  type ProcurementComparisonScoreRequest,
  type ProcurementComparisonSummaryDTO,
  type ProcurementNegotiationDTO,
  type ProcurementNegotiationRequest,
  type ProcurementRfqCancelAwardProcessRequest,
  type RecordQuoteLogisticsRequest,
  type RFQDTO,
} from '@erpx/api'

import { api } from '@/api/client'

/** Permission codes the comparison screens check (mirrors `internal/procurement`). */
export const COMPARISON_PERMISSIONS = {
  /** Every read: the RFQ, the comparison versions, the negotiation rounds, the awards. */
  rfqRead: 'procurement.rfq.read',
  /** Build or rebuild a comparison version; configure its landed cost. */
  comparisonBuild: 'procurement.comparison.build',
  /** Enter and submit technical scores (the evaluator must also be on the RFQ's panel). */
  rfqEvaluate: 'procurement.rfq.evaluate',
  /** Record a negotiation round. */
  negotiationRecord: 'procurement.negotiation.record',
  /** Record a quote's freight / duty / other cost and Incoterm. */
  rfqQuote: 'procurement.rfq.quote',
  /** End the award process without an award (it cancels the RFQ). */
  rfqCancel: 'procurement.rfq.cancel',
  /** Open a sealed RFQ (with a witness). */
  rfqOpen: 'procurement.rfq.open',
} as const

export const COMPARISON_KEYS = {
  all: ['procurement', 'comparisons'] as const,
  versions: (rfqId: number) => ['procurement', 'comparisons', 'rfq', rfqId, 'versions'] as const,
  one: (id: number) => ['procurement', 'comparisons', 'detail', id] as const,
  negotiations: (rfqId: number) => ['procurement', 'comparisons', 'rfq', rfqId, 'negotiations'] as const,
  quotes: (rfqId: number) => ['procurement', 'comparisons', 'rfq', rfqId, 'quotes'] as const,
  rfqs: (params: Record<string, unknown>) => ['procurement', 'comparisons', 'rfqs', params] as const,
} as const

/** The three comparison bases the server accepts (`ComparisonBasis`). */
export const COMPARISON_BASES = ['landed_total', 'unit_price', 'tco'] as const
export type ComparisonBasisValue = (typeof COMPARISON_BASES)[number]

/** Unsuccessful-bidder feedback reasons (`ProcurementAwardFeedbackInput.reasonCode` oneof). */
export const FEEDBACK_REASONS = ['price', 'technical', 'delivery', 'compliance', 'terms', 'other'] as const

/* ----------------------------------------------------------------- reads */

export function listComparisonVersions(rfqId: number): Promise<ProcurementComparisonSummaryDTO[]> {
  return procurementRfqComparisonVersions(api(), { id: String(rfqId) })
}

export function getComparison(id: number): Promise<ProcurementComparisonDTO> {
  return procurementComparisonGet(api(), { id: String(id) })
}

export function listNegotiations(rfqId: number): Promise<ProcurementNegotiationDTO[]> {
  return procurementRfqNegotiations(api(), { id: String(rfqId) })
}

/* ---------------------------------------------------------------- writes */

/**
 * Build the RFQ's first comparison, or a new version (the previous one is kept, superseded).
 * The RFQ must be closed or under evaluation, its prices revealed, and no award may stand.
 */
export function buildComparison(
  rfqId: number,
  body: ProcurementComparisonBuildRequest,
  idempotencyKey: string,
): Promise<ProcurementComparisonDTO> {
  return procurementRfqComparisonBuild(api(), { id: String(rfqId) }, body, idempotencyKey)
}

/** Freight, duty, insurance, running cost and payment days per response. Draft only. */
export function setLandedCost(
  comparisonId: number,
  body: ProcurementComparisonLandedCostRequest,
  idempotencyKey: string,
): Promise<ProcurementComparisonDTO> {
  return procurementComparisonLandedCost(api(), { id: String(comparisonId) }, body, idempotencyKey)
}

/** The calling evaluator's technical scores; editable until they submit. */
export function recordComparisonScores(
  comparisonId: number,
  body: ProcurementComparisonScoreRequest,
  idempotencyKey: string,
): Promise<ProcurementComparisonDTO> {
  return procurementComparisonScoresRecord(api(), { id: String(comparisonId) }, body, idempotencyKey)
}

/** Lock the calling evaluator's scores. Once submitted they are never altered. */
export function submitComparisonScores(
  comparisonId: number,
  idempotencyKey: string,
): Promise<ProcurementComparisonDTO> {
  return procurementComparisonScoresSubmit(api(), { id: String(comparisonId) }, idempotencyKey)
}

/** One negotiation round with one supplier; the comparison is rebuilt as a new version. */
export function recordNegotiation(
  rfqId: number,
  body: ProcurementNegotiationRequest,
  idempotencyKey: string,
): Promise<ProcurementNegotiationDTO> {
  return procurementRfqNegotiate(api(), { id: String(rfqId) }, body, idempotencyKey)
}

/**
 * A response's own freight / duty / other cost and Incoterm (absorbs W5-P4). The next
 * comparison version picks them up as that response's landed-cost defaults.
 */
export function recordQuoteLogistics(
  rfqId: number,
  quoteId: number,
  body: RecordQuoteLogisticsRequest,
  idempotencyKey: string,
): Promise<void> {
  return procurementRfqQuotesLogistics(
    api(),
    { id: String(rfqId), qid: String(quoteId) },
    body,
    idempotencyKey,
  )
}

/** End the award process without an award: any award in flight is cancelled, every bidder told. */
export function cancelAwardProcess(
  rfqId: number,
  body: ProcurementRfqCancelAwardProcessRequest,
  idempotencyKey: string,
): Promise<RFQDTO> {
  return procurementRfqCancelAwardProcess(api(), { id: String(rfqId) }, body, idempotencyKey)
}
